import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add, Edit, Delete, Refresh, FiberManualRecord } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';
import {
  fetchDevices,
  fetchClientGroups,
  createDeviceThunk,
  updateDeviceThunk,
  deleteDeviceThunk,
  toggleWatchThunk,
  clearDeviceError,
  clearDeviceMessage,
} from '../../store/slices/adminDeviceSlice';
import { AdminDevice, DeviceCreateRequest, DeviceUpdateRequest } from '../../types/adminDevice';
import DeviceFormDialog from './DeviceFormDialog';

const DeviceManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { adminApiKey } = useAppSelector((state: RootState) => state.adminAuth);
  const { devices, clientGroups, loading, saving, error, actionMessage } = useAppSelector(
    (state: RootState) => state.adminDevices
  );

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingDevice, setEditingDevice] = useState<AdminDevice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminDevice | null>(null);

  useEffect(() => {
    if (adminApiKey) {
      dispatch(fetchDevices(adminApiKey));
      dispatch(fetchClientGroups(adminApiKey));
    }
  }, [adminApiKey, dispatch]);

  const handleRefresh = () => {
    if (adminApiKey) {
      dispatch(fetchDevices(adminApiKey));
      dispatch(fetchClientGroups(adminApiKey));
    }
  };

  const handleOpenCreate = () => {
    setFormMode('create');
    setEditingDevice(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (device: AdminDevice) => {
    setFormMode('edit');
    setEditingDevice(device);
    setFormOpen(true);
  };

  const handleCreate = async (payload: DeviceCreateRequest) => {
    if (!adminApiKey) return;
    const result = await dispatch(createDeviceThunk({ payload, adminApiKey }));
    if (createDeviceThunk.fulfilled.match(result)) {
      setFormOpen(false);
      dispatch(fetchClientGroups(adminApiKey));
    }
  };

  const handleUpdate = async (serialNo: string, payload: DeviceUpdateRequest) => {
    if (!adminApiKey) return;
    const result = await dispatch(updateDeviceThunk({ serialNo, payload, adminApiKey }));
    if (updateDeviceThunk.fulfilled.match(result)) {
      setFormOpen(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!adminApiKey || !deleteTarget) return;
    await dispatch(deleteDeviceThunk({ serialNo: deleteTarget.serialNo, adminApiKey }));
    setDeleteTarget(null);
  };

  const handleToggleWatch = (device: AdminDevice) => {
    if (!adminApiKey) return;
    dispatch(toggleWatchThunk({ serialNo: device.serialNo, enabled: !device.watchEnabled, adminApiKey }));
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Device Management</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<Refresh />} onClick={handleRefresh} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
              Add Device
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearDeviceError())}>
            {error}
          </Alert>
        )}
        {actionMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearDeviceMessage())}>
            {actionMessage}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Online</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Client Group</TableCell>
                  <TableCell>City / Location</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Sent Route</TableCell>
                  <TableCell align="center">Watch</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.serialNo} hover>
                    <TableCell>
                      <Tooltip title={device.online ? 'Online (subscribed)' : 'Offline'}>
                        <FiberManualRecord
                          fontSize="small"
                          sx={{ color: device.online ? 'success.main' : 'grey.400' }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{device.serialNo}</TableCell>
                    <TableCell>
                      {device.clientName ? (
                        <Chip size="small" label={`${device.clientName} (${device.clientId})`} />
                      ) : (
                        <Chip size="small" color="warning" label="Unassigned" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {[device.city, device.location].filter(Boolean).join(' / ') || '—'}
                    </TableCell>
                    <TableCell>{device.model || '—'}</TableCell>
                    <TableCell>{device.version || '—'}</TableCell>
                    <TableCell>{device.status || '—'}</TableCell>
                    <TableCell>
                      {device.lastSentRouteShipmentNumber ? (
                        <Box>
                          <Typography variant="body2">{device.lastSentRouteShipmentNumber}</Typography>
                          {device.lastSentRouteDate && (
                            <Typography variant="caption" color="text.secondary">
                              {device.lastSentRouteDate}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        device.lastSentRouteDate || '—'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={device.watchEnabled}
                        onChange={() => handleToggleWatch(device)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenEdit(device)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(device)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {devices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No devices found. Click "Add Device" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <DeviceFormDialog
        open={formOpen}
        mode={formMode}
        device={editingDevice}
        clientGroups={clientGroups}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete device?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete device <strong>{deleteTarget?.serialNo}</strong>? This will
            also remove its client group mapping. Devices that still own routes cannot be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceManagement;
